import { ListViewColumn, ListViewProfile, ListViewType } from 'models/organization';

export type OrderListColumnKey =
    | 'status'
    | 'orderId'
    | 'orderIdOnly'
    | 'internalOrderId'
    | 'patient'
    | 'constructions'
    | 'customer'
    | 'price'
    | 'created'
    | 'deliveryDeadline'
    | 'orderTag'
    | 'timeInStep'
    | 'workflow'
    | 'workStatus'
    | 'materials'
    | 'delivery'
    | 'internalDeliveryDate'
    | 'externalDeliveryDate'
    | 'sentToCustomer'
    | 'panNumber';

export type OrderListColumnDefinition = {
    key: OrderListColumnKey;
    labelKey: string;
    i18nNamespace: 'common' | 'orders' | 'workflow' | 'transport';
    descriptionKey: string;
    defaultWidth: number;
    sortable?: { field: string };
};

export const ORDER_LIST_FRAMEWORK_CELL_WIDTHS = {
    flag: 40,
    iconArea: 160,
    moreVert: 40,
} as const;

export const ORDER_LIST_COLUMN_MIN_WIDTH = 60;
export const ORDER_LIST_COLUMN_MAX_WIDTH = 800;

export const ORDER_LIST_COLUMN_DEFINITIONS: Record<OrderListColumnKey, OrderListColumnDefinition> = {
    status: {
        key: 'status',
        labelKey: 'STATUS',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_STATUS',
        defaultWidth: 155,
    },
    orderId: {
        key: 'orderId',
        labelKey: 'ORDER_ID',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_ORDER_ID',
        defaultWidth: 105,
    },
    orderIdOnly: {
        key: 'orderIdOnly',
        labelKey: 'ORDER_ID',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_ORDER_ID_ONLY',
        defaultWidth: 95,
        sortable: { field: 'orderId' },
    },
    internalOrderId: {
        key: 'internalOrderId',
        labelKey: 'INTERNAL_ORDER_ID',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_INTERNAL_ORDER_ID',
        defaultWidth: 110,
        sortable: { field: 'internalOrderId' },
    },
    patient: {
        key: 'patient',
        labelKey: 'PATIENT',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_PATIENT',
        defaultWidth: 220,
    },
    constructions: {
        key: 'constructions',
        labelKey: 'CONSTRUCTIONS',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_CONSTRUCTIONS',
        defaultWidth: 160,
    },
    customer: {
        key: 'customer',
        labelKey: 'CUSTOMER',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_CUSTOMER',
        defaultWidth: 260,
    },
    price: {
        key: 'price',
        labelKey: 'PRICE',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_PRICE',
        defaultWidth: 130,
    },
    created: {
        key: 'created',
        labelKey: 'CREATED',
        i18nNamespace: 'common',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_CREATED',
        defaultWidth: 165,
        sortable: { field: 'created.on' },
    },
    deliveryDeadline: {
        key: 'deliveryDeadline',
        labelKey: 'DELIVERY_DEADLINE',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_DELIVERY_DEADLINE',
        defaultWidth: 160,
    },
    orderTag: {
        key: 'orderTag',
        labelKey: 'ORDER_TAG',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_ORDER_TAG',
        defaultWidth: 90,
    },
    timeInStep: {
        key: 'timeInStep',
        labelKey: 'TIME_IN_STEP',
        i18nNamespace: 'workflow',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_TIME_IN_STEP',
        defaultWidth: 160,
        sortable: { field: 'workFlow.currentStep.dateTime' },
    },
    workflow: {
        key: 'workflow',
        labelKey: 'WORKFLOW',
        i18nNamespace: 'workflow',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_WORKFLOW',
        defaultWidth: 180,
    },
    workStatus: {
        key: 'workStatus',
        labelKey: 'WORK_STATUS',
        i18nNamespace: 'workflow',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_WORK_STATUS',
        defaultWidth: 120,
    },
    materials: {
        key: 'materials',
        labelKey: 'MATERIALS',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_MATERIALS',
        defaultWidth: 180,
    },
    delivery: {
        key: 'delivery',
        labelKey: 'DELIVERY',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_DELIVERY',
        defaultWidth: 120,
        sortable: { field: 'deliveryDetails.date' },
    },
    internalDeliveryDate: {
        key: 'internalDeliveryDate',
        labelKey: 'DEADLINE',
        i18nNamespace: 'transport',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_INTERNAL_DEADLINE',
        defaultWidth: 120,
        sortable: { field: 'deliveryDetails.internalDeliveryDate' },
    },
    externalDeliveryDate: {
        key: 'externalDeliveryDate',
        labelKey: 'EXTERNAL_DELIVERY_DATE',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_EXTERNAL_DELIVERY_DATE',
        defaultWidth: 120,
        sortable: { field: 'deliveryDetails.externalDeliveryDate' },
    },
    sentToCustomer: {
        key: 'sentToCustomer',
        labelKey: 'SENT_TO_CUSTOMER',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_SENT_TO_CUSTOMER',
        defaultWidth: 130,
        sortable: { field: 'manualSelectedSentDate' },
    },
    panNumber: {
        key: 'panNumber',
        labelKey: 'PAN_NUMBER',
        i18nNamespace: 'orders',
        descriptionKey: 'lab-settings:LIST_VIEW_COLUMN_DESC_PAN_NUMBER',
        defaultWidth: 130,
    },
};

export const MAIN_ORDER_LIST_DEFAULT_COLUMN_KEYS: OrderListColumnKey[] = [
    'status',
    'orderTag',
    'orderId',
    'patient',
    'constructions',
    'customer',
    'price',
    'created',
    'deliveryDeadline',
];

export const WORKFLOW_ORDER_LIST_DEFAULT_COLUMN_KEYS: OrderListColumnKey[] = [
    'orderTag',
    'workStatus',
    'timeInStep',
    'orderId',
    'patient',
    'constructions',
    'materials',
    'customer',
    'delivery',
    'internalDeliveryDate',
    'externalDeliveryDate',
];

export const ALL_ORDER_LIST_COLUMN_KEYS: OrderListColumnKey[] = Object.keys(
    ORDER_LIST_COLUMN_DEFINITIONS,
) as OrderListColumnKey[];

const buildSystemDefault = (
    id: string,
    name: string,
    columnKeys: OrderListColumnKey[],
    showIconArea: boolean,
): ListViewProfile => ({
    _id: id,
    name,
    listType: ListViewType.LAB_ORDER_LIST,
    columns: columnKeys.map((key) => ({ key, visible: true })),
    showIconArea,
});

export const SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE: ListViewProfile = buildSystemDefault(
    'SYSTEM_DEFAULT_MAIN',
    'SYSTEM_DEFAULT_MAIN',
    MAIN_ORDER_LIST_DEFAULT_COLUMN_KEYS,
    true,
);

export const SYSTEM_DEFAULT_WORKFLOW_ORDER_LIST_PROFILE: ListViewProfile = buildSystemDefault(
    'SYSTEM_DEFAULT_WORKFLOW',
    'SYSTEM_DEFAULT_WORKFLOW',
    WORKFLOW_ORDER_LIST_DEFAULT_COLUMN_KEYS,
    false,
);

const SYSTEM_DEFAULT_PROFILE_IDS = new Set([
    SYSTEM_DEFAULT_MAIN_ORDER_LIST_PROFILE._id,
    SYSTEM_DEFAULT_WORKFLOW_ORDER_LIST_PROFILE._id,
]);

export const isSystemDefaultProfile = (profile: ListViewProfile): boolean =>
    SYSTEM_DEFAULT_PROFILE_IDS.has(profile._id);

export const isKnownOrderListColumnKey = (key: string): key is OrderListColumnKey =>
    Object.prototype.hasOwnProperty.call(ORDER_LIST_COLUMN_DEFINITIONS, key);

export const getActiveOrderListColumnKeys = (profile: ListViewProfile): OrderListColumnKey[] => {
    const seen = new Set<string>();
    const ordered: OrderListColumnKey[] = [];

    for (const column of profile.columns) {
        if (!column.visible) continue;
        if (!isKnownOrderListColumnKey(column.key)) continue;
        if (seen.has(column.key)) continue;

        ordered.push(column.key);
        seen.add(column.key);
    }

    return ordered;
};

export const mergeProfileWithRegistry = (profile: ListViewProfile): ListViewColumn[] => {
    const profileKeys = new Set(profile.columns.map((column) => column.key));
    const merged: ListViewColumn[] = profile.columns
        .filter((column) => isKnownOrderListColumnKey(column.key))
        .map((column) => ({ key: column.key, visible: column.visible }));

    for (const key of ALL_ORDER_LIST_COLUMN_KEYS) {
        if (!profileKeys.has(key)) {
            merged.push({ key, visible: false });
        }
    }

    return merged;
};
